import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { observarPonteDoMedian, pintarBarraDeStatus, PISO_DO_APP } from '@/lib/barraDeStatus';

type ThemeMode = 'light' | 'dark' | 'system';
type FontScale = 'P' | 'G';

export interface FontSizeClasses {
  tiny: string;
  xs: string;
  sm: string;
  base: string;
  lg: string;
  xl: string;
  '2xl': string;
}

interface ThemeContextType {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  fontScale: FontScale;
  setFontScale: (scale: FontScale) => void;
  fontSize: FontSizeClasses;
}

const fontSizeScales: Record<FontScale, FontSizeClasses> = {
  P: {
    tiny: 'text-[10px]',
    xs: 'text-[11px]',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
  },
  G: {
    tiny: 'text-xs',
    xs: 'text-sm',
    sm: 'text-lg',
    base: 'text-xl',
    lg: 'text-2xl',
    xl: 'text-3xl',
    '2xl': 'text-4xl',
  },
};

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ondiet-theme-mode');
      if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
    }
    return 'system';
  });

  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>(() =>
    mode === 'system' ? getSystemTheme() : mode
  );

  const [fontScale, setFontScaleState] = useState<FontScale>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ondiet-font-scale');
      if (stored === 'P' || stored === 'G') return stored;
    }
    // ⚠️ O PADRÃO É 'G' desde 2026-08-12, por decisão do Thiago.
    //
    // Quem já escolheu no Perfil mantém a escolha — o `localStorage` acima vem
    // primeiro, e essa ordem é a garantia. O que muda é o padrão de quem nunca
    // mexeu.
    //
    // Isto só pôde entrar DEPOIS do portão dos Termos parar de depender de
    // `onScroll` (mesmo PR): letra grande muda a altura do texto, e um portão
    // que dependia de rolagem para destravar era o pior lugar do app para
    // mudar tamanho de fonte por padrão. Trocar a ordem dos dois teria trancado
    // usuário novo para fora.
    return 'G';
  });

  // Listen for system theme changes when mode is 'system'
  useEffect(() => {
    if (mode !== 'system') {
      setResolvedMode(mode);
      return;
    }
    setResolvedMode(getSystemTheme());
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setResolvedMode(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode]);

  // Apply resolved theme to DOM
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('ondiet-theme-mode', mode);

    // Update background color on html/body to match theme (override critical CSS)
    // Piso do sistema: tinta quente e papel. Eram #000000 e #FEFEFE — os dois
    // banidos pelo design system, e este é o valor que pinta a barra do
    // navegador e o fundo antes do CSS carregar, então é o primeiro que a
    // pessoa vê. Tem que bater com `--background` do `index.css`.
    const bgColor = PISO_DO_APP[resolvedMode];
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.style.backgroundColor = bgColor;
    document.documentElement.style.colorScheme = resolvedMode === 'dark' ? 'dark' : 'light';

    // Update theme-color meta tag for iOS overscroll
    document.querySelectorAll('meta[name="theme-color"]').forEach(meta => {
      meta.setAttribute('content', bgColor);
    });

    // ⚠️ E A BARRA DO SISTEMA POR CIMA DE TUDO ISSO — que NÃO é coberta por
    // nada acima. Dentro do Median ela é uma view nativa: ignora o
    // `theme-color`, ignora o `backgroundColor` do <html> e continuava preta
    // sobre o piso tinta. Ver `lib/barraDeStatus.ts`; fora do Median é no-op.
    observarPonteDoMedian();
    pintarBarraDeStatus(resolvedMode);
  }, [resolvedMode, mode]);

  useEffect(() => {
    localStorage.setItem('ondiet-font-scale', fontScale);
    // Marca a RAIZ para o CSS poder reagir. As telas legado escalam por classe
    // Tailwind (`fontSize.*`), mas o Plano escala por token `--sv-type-*` em CSS
    // — e CSS não lê estado de React. Sem este atributo o Plano IGNORA o botão,
    // que foi o que aconteceu até 2026-08-07: quem aumentava a fonte via o app
    // inteiro crescer menos a tela em que mais se lê número.
    //
    // Só "G" é marcado; "P" limpa o atributo, para o padrão do CSS ser o default
    // e não depender de um valor escrito.
    const raiz = document.documentElement;
    if (fontScale === 'G') raiz.dataset.fontScale = 'G';
    else delete raiz.dataset.fontScale;
  }, [fontScale]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
  };

  const setFontScale = (scale: FontScale) => {
    setFontScaleState(scale);
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        resolvedMode,
        setMode,
        fontScale,
        setFontScale,
        fontSize: fontSizeScales[fontScale],
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
